<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <ToggleSwitch v-model="showActive" label-on="Active" label-off="All" />
      <Button
        icon="far fa-plus"
        :text="`New ${programName}`"
        data-cy="new-schedule"
        @click="openModal()"
      />
    </div>

    <div v-if="loading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin"></i> Loading...
    </div>

    <div v-else-if="filteredSchedules.length === 0" class="text-muted py-4">
      No {{ programNamePlural.toLowerCase() }} found.
    </div>

    <ProgressBillingScheduleCard
      v-for="schedule in filteredSchedules"
      :key="schedule.id"
      :schedule="schedule"
      @edit="handleEdit"
      @copy="handleCopy"
      @unarchive="handleUnarchive"
      @deactivate="handleDeactivate"
    />

    <ProgressBillingScheduleModal
      v-if="isModalOpen"
      :schedule-id="currentScheduleId"
      @hide-modal="closeModal"
      @reload="$emit('reload')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { trackEvent } from "@/lib/pendo";

import Button from "@/components/Button.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import { useProgressBillingLabels } from "@/composables/useProgressBillingLabels";
import ProgressBillingScheduleCard from "./ProgressBillingScheduleCard.vue";
import ProgressBillingScheduleModal from "./ProgressBillingScheduleModal.vue";

const { programName, programNamePlural } = useProgressBillingLabels();

interface Installment {
  id: number;
  label: string;
  percentage: number;
  position: number;
}

interface Schedule {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  system_default: boolean;
  installments: Installment[];
}

const store = useStore();

const props = defineProps<{
  schedules: Schedule[];
  loading: boolean;
}>();

const emit = defineEmits<{
  reload: [];
}>();

const showActive = ref(true);
const isModalOpen = ref(false);
const currentScheduleId = ref<number | null>(null);

function openModal(scheduleId: number | null = null) {
  currentScheduleId.value = scheduleId;
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
  currentScheduleId.value = null;
}

const filteredSchedules = computed(() => {
  if (showActive.value) {
    return props.schedules.filter((t) => t.is_active);
  }
  return props.schedules;
});

function handleEdit(schedule: Schedule) {
  openModal(schedule.id);
}

async function handleCopy(schedule: Schedule) {
  await store.dispatch("copyProgressBillingSchedule", schedule.id);
  emit("reload");
}

async function handleUnarchive(schedule: Schedule) {
  await store.dispatch("unarchiveProgressBillingSchedule", schedule.id);
  emit("reload");
}

async function handleDeactivate(schedule: Schedule) {
  await store.dispatch("deleteProgressBillingSchedule", schedule.id);
  trackEvent("spb_template_archived", { schedule_id: schedule.id });
  emit("reload");
}
</script>
